import React from 'react';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ChartProps } from '@app-types/visualization';
import sharedStyles from './charts.module.css';

interface TooltipPayloadEntry {
  name: string;
  value: number;
  color: string;
}

interface BarTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
  xAxisLabel: string;
}

const CustomTooltip: React.FC<BarTooltipProps> = ({ active, payload, label, xAxisLabel }) => {
  if (active && payload?.length) {
    return (
      <div className={sharedStyles.customTooltip}>
        <p className={sharedStyles.tooltipLabel}>{`${xAxisLabel}: ${label}`}</p>
        {payload.map((entry) => (
          <p key={`legend-${entry.name}-${entry.value}-${entry.color}`} style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value.toLocaleString()}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DualAxisBarChart: React.FC<ChartProps> = ({ data, config, colors = ['#3b82f6', '#10b981'] }) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  if (!config?.yAxis?.primary || !config?.yAxis?.secondary) {
    console.warn('DualAxisBarChart requires primary and secondary yAxis configuration');
    return <div>Chart configuration error: Missing dual axis setup</div>;
  }

  const primaryColumns = config.yAxis.primary.columns || [];
  const secondaryColumns = config.yAxis.secondary.columns || [];
  const xAxisColumn = config.xAxis?.column || Object.keys(data[0])[0];

  // Format data for Recharts
  const formattedData = data.map((item) => {
    const formatted = { [xAxisColumn]: item[xAxisColumn] };

    // Add primary axis data
    primaryColumns.forEach((col) => {
      formatted[col] = Number(item[col]) || 0;
    });

    // Add secondary axis data
    secondaryColumns.forEach((col) => {
      formatted[col] = Number(item[col]) || 0;
    });

    return formatted;
  });

  const xAxisLabel = config.xAxis?.label || 'Category';

  return (
    <div className={sharedStyles.chartContainer}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={formattedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey={xAxisColumn}
            tick={{ fontSize: 12 }}
            angle={data.length > 8 ? -45 : 0}
            textAnchor={data.length > 8 ? 'end' : 'middle'}
            height={data.length > 8 ? 80 : 60}
          />

          {/* Primary Y-Axis (left) */}
          <YAxis yAxisId="primary" tick={{ fontSize: 12 }} tickFormatter={(value) => value.toLocaleString()} />

          {/* Secondary Y-Axis (right) */}
          <YAxis
            yAxisId="secondary"
            orientation="right"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => value.toLocaleString()}
          />

          <Tooltip content={<CustomTooltip xAxisLabel={xAxisLabel} />} />
          <Legend />

          {/* Primary axis bars */}
          {primaryColumns.map((column, index) => (
            <Bar
              key={`primary-${column}`}
              yAxisId="primary"
              dataKey={column}
              name={config.yAxis.primary.labels?.[index] || column.replace(/_/g, ' ')}
              fill={colors[index] || colors[0]}
              radius={[2, 2, 0, 0]}
            />
          ))}

          {/* Secondary axis bars */}
          {secondaryColumns.map((column, index) => (
            <Bar
              key={`secondary-${column}`}
              yAxisId="secondary"
              dataKey={column}
              name={config.yAxis.secondary.labels?.[index] || column.replace(/_/g, ' ')}
              fill={colors[primaryColumns.length + index] || colors[1]}
              radius={[2, 2, 0, 0]}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DualAxisBarChart;
